// utils/elementFormat/phone.js
//
// How a `phone` element PARSES and RENDERS (docs/plans/data-types.md,
// decision 6 and the frozen wire contract): one number, one stored spelling
// (E.164, `+12025550123`), and one display rule that depends on where the
// reader is — a local number reads locally, a foreign one reads with its
// country code.
//
// This file is vendored byte-identical into keepr-web at
// `src/utils/elementFormat/phone.js`, the way utils/units/ and
// utils/formula/formula.js are, so the web's inline validation and the API's
// 400 agree to the digit: if the form says the number is good, the save
// cannot disagree. MIRRORED in keepr-web — change them together.
//
// The ONE dependency, and the only file in either repo allowed to have it, is
// `libphonenumber-js/min` — the metadata set that knows every region's
// numbering plan. Requiring it from exactly one place is what makes "both
// repos parse identically" a fact rather than a hope: there is one call site
// to keep in step, and the rest of the platform sees plain strings.
//
// `/min` and not `/max` on purpose: the min metadata validates national
// LENGTH per region, which is what refuses "(202) 555-01" and accepts
// "(202) 555-0123". The extra megabyte of `/max` buys number TYPE detection
// (mobile vs landline), which decision 6 puts out of scope — and the web
// pays that megabyte in every bundle.
//
// Nothing here throws, reads a request, or knows about Mongo or Boom: every
// function answers with a value, and the caller decides whether a `valid:
// false` is a 400, a red input border, or legacy data rendered as typed.

'use strict';

const { parsePhoneNumberFromString, isSupportedCountry } = require('libphonenumber-js/min');

// The region a national number ("202 555 0123") is read against when the
// element's author did not choose one. Also the platform default the card
// editor seeds — the contract's `options.country` default.
const DEFAULT_COUNTRY = 'US';

const toStr = v => (v === null || v === undefined ? '' : String(v));

/**
 * The region code an element's `country` option names, normalised to the
 * upper-case ISO 3166-1 alpha-2 spelling libphonenumber wants, or null when
 * it names no region libphonenumber knows.
 *
 * Exported because this module is the only one permitted to require
 * libphonenumber: the card-definition option gate (utils/typedElements.js)
 * and the web's option editor both ask HERE whether a country code is real,
 * rather than carrying a second, drifting country list.
 */
function normalizeCountry(country) {
	const code = toStr(country).trim().toUpperCase();
	if (code.length !== 2) return null;
	return isSupportedCountry(code) ? code : null;
}

/** True when `country` names a region libphonenumber knows (any case). */
function isSupportedRegion(country) {
	return normalizeCountry(country) !== null;
}

/**
 * parsePhone(raw, country) -> { e164, valid, country }
 *
 *   e164     the stored spelling ('+12025550123') — '' unless `valid`, so a
 *            caller that stores the result without checking cannot persist
 *            something the platform would refuse to read back.
 *   valid    the number parses AND is a real number in its numbering plan.
 *            A blank input is NOT valid here: emptiness is the caller's
 *            question (an empty phone element stores '', it is not an error),
 *            and conflating the two would make every untouched form field a
 *            validation failure.
 *   country  the region the NUMBER turned out to belong to ('US', 'GB'), not
 *            the region it was parsed against — a `+44…` typed into a US
 *            element is a British number. null when unknown (some ranges,
 *            such as +800 international freephone, belong to no country).
 *
 * `country` is the element's `options.country`; an unknown or missing one
 * falls back to DEFAULT_COUNTRY rather than refusing to parse, because a
 * number written in full international form ('+44 20 7183 8750') does not
 * need a region at all and should not fail on the element's misconfiguration.
 */
function parsePhone(raw, country) {
	const text = toStr(raw).trim();
	if (!text) return { e164: '', valid: false, country: null };

	const region = normalizeCountry(country) || DEFAULT_COUNTRY;
	let parsed;
	try {
		parsed = parsePhoneNumberFromString(text, region);
	} catch (e) {
		// parsePhoneNumberFromString is documented not to throw, but this
		// module sits on both the API write path and every keystroke of a web
		// input: a metadata surprise must degrade to "not valid", never to a
		// 500 or a blank screen.
		parsed = null;
	}
	if (!parsed || !parsed.isValid()) return { e164: '', valid: false, country: null };
	return { e164: parsed.number, valid: true, country: parsed.country || null };
}

/**
 * formatPhone(e164, country) -> string
 *
 * National format ('(202) 555-0123') when the number belongs to the
 * ELEMENT's country, international ('+44 20 7183 8750') when it does not —
 * the rule a reader applies without being taught it: your own country's
 * numbers are written the way you dial them, everyone else's carry their
 * country code.
 *
 * A value that is not a VALID number is returned as typed, never as '' and
 * never reformatted — a legacy element may hold '555-1234' or 'ext. 4021'
 * from before the type existed. libphonenumber will happily hand back a
 * half-parsed object for such a string (digits salvaged, the rest discarded),
 * and rendering that would silently rewrite the user's data on screen;
 * showing them what is actually stored is the honest answer.
 */
function formatPhone(e164, country) {
	const text = toStr(e164).trim();
	if (!text) return '';

	const region = normalizeCountry(country) || DEFAULT_COUNTRY;
	let parsed;
	try {
		parsed = parsePhoneNumberFromString(text, region);
	} catch (e) {
		parsed = null;
	}
	if (!parsed || !parsed.isValid()) return text;
	return parsed.country && parsed.country === region ? parsed.formatNational() : parsed.formatInternational();
}

/**
 * telUrl(e164) -> 'tel:+12025550123' | ''
 *
 * The href behind the Call action (decision 8, "values carry their actions").
 * E.164 is already the shape `tel:` wants and contains nothing that needs
 * escaping, so the stored value goes through verbatim; a blank value has no
 * action, which is how the caller knows not to render the trigger.
 */
function telUrl(e164) {
	const text = toStr(e164).trim();
	return text ? `tel:${text}` : '';
}

module.exports = {
	DEFAULT_COUNTRY,
	parsePhone,
	formatPhone,
	telUrl,
	normalizeCountry,
	isSupportedRegion,
};
